<template>
  <div class="card">
    <header class="card-header">
      <ul class="tab-heads">
        <li
          class="tab-head"
          v-for="tab in tabs"
          :key="tab"
          v-bind:class="{
            'tab-head--active': activeTab === tab
          }"
          v-on:click="switchTab(tab);"
        >
          <slot :name="tabHeadSlotName(tab)">{{ tab }} </slot>
        </li>
      </ul>

      <div id="footnote-trigger">      
          <span>References link to sources. Hover on <img width=15 style="display: inline; padding-top: 1px; margin-left: 3px; margin-right: 3px" src="../assets/i-icon-white.svg" /> icons or 
          <a @click="showModal = true" class="show-modal-trigger">
            click here
          </a>
            to view notes about columns.
          </span>
      </div>
      <div id="mobile-footnote-trigger">
        <a @click="showModal = true" class="show-modal-trigger">
          <span>Show notes</span>
        </a>
      </div>

      <!-- Show modal when click trigger in the tab header -->
        <Modal 
        v-if="showModal" 
        @close="showModal = false">
          <h3 slot="header">{{modalContent.header}}</h3>
          <div slot="body">
            <div v-for="(item, value) in modalContent.body"
              :key="item" class="single-footnote">
                <span class="footnote-title">{{value}}</span>
                <span class="footnote-body">
                  {{item}}
                </span>
            </div>
          </div>
        </Modal>
    </header>
    <main class="card-body">
      <div class="tab-panel">
        <slot :name="tabPanelSlotName"> </slot>
      </div>
    </main>
  </div>
</template>

<script>
import Modal from './Modal.vue';

export default {
  name: "TabCard",
  components: {
    Modal
  },
  props: {
    initialTab: String,
    tabs: Array
  },
  data() {
    return {
      activeTab: this.initialTab,
      showModal: false,
      cas9: 'cas notes',
      deam: 'deam notes',
      arch: 'arch notes'
    };
  },
  computed: {
    tabPanelSlotName() {
      return `tab-panel-${this.activeTab}`;
    },
    modalContent() {
      let header, body;
      if (this.tabPanelSlotName == "tab-panel-cas9") {
        header = "Cas proteins compatible with base editing";
        body = {
          "Editing window (BE4max, ABEmax): ": "The editing window symbols refer to the key to the right of the table. These windows are estimates based on available data from publications and can vary depending on target site and experimental conditions. The height of the curve reflects expected relative editing efficiencies at each position of the protospacer. The position of the PAM is highlighted in purple Green = CBE-specific, orange = ABE-specific.",
          "PAM scope: ": "Targetable PAMs are highlighted in purple. Partially covered PAMs or non-consensus PAMs that have been reported but not thoroughly verified for base editing are highlighted in grey. The nucleotide on the left reflects the first non-degenerate nucleotide and the nucleotide codes on top indicate the following two.",
          "PAM scope consensus: ": "cRegions shaded grey indicate the positions compared in the PAM scope column. Note that certain PAMs will shift the positioning of the protospacer."
        }
        return {header, body}
      } else if (this.tabPanelSlotName == "tab-panel-deam") {
        header = "Base editing deaminases and their properties"
        body = {
          "Deaminase: ": "Many of the publications reporting these deaminases examined other alternative deaminases. Deaminases that are not well-characterized or were not recommended by the original authors are not reported here or are described in Supplementary Table 3 for clarity.",
          "Approximate editing window: ": "The extent to which editing windows have been characterized varies. The editing windows shown here are estimates based on available data from the original publications and can vary with target site and experimental conditions. Dark regions indicate the editing window resulting in optimal editing efficiencies, and lighter regions indicate regions where less editing has been observed.",
          "Sequence Preference: ": "Strong sequence preference or anti-preference is shown in dark green/orange or dark grey, respectively. Moderate or weak sequence preference or anti-preference is shown in light green/orange or light grey, respectively. No preference is shown in white. Diagonal lines signify not characterized.",
          "Off-target editing: ": "Off-target DNA editing characterization indicates Cas-dependent and/or Cas-independent DNA off-targets observed relative to canonical CBE (BE3 with rAPOBEC1) or ABE (ABE7.10). Light grey/green arrows indicate a moderate increase or decrease, dark grey/green arrows indicate a strong increase or decrease, and a dash indicates no significant change in off-target editing relative to canonical CBE (BE3 with rAPOBEC1) or ABE (ABE with TadA(7.10)). Diagonal lines indicate that the sequence context and/or off-target editing with these deaminases are not well-characterized. See also note: These deaminases have recently been characterized to be able to elicit programmable C-to-G transversion edits in certain base editor architectures (CGBEs)50,51,53.",
          "Beacon 1 and Beacon 2 next to editing window graphic (not on all tables): ": "These deaminases have only been validated with Cas12a, which has a 5’ PAM and a 23 nt protospacer. The approximate editing window is shown relative to these Cas properties.",
          '"Also see" note: ': "These deaminases have recently been characterized to be able to elicit programmable C-to-G transversion edits in certain base editor architectures (CGBEs)50,51,53."
        }
        return {header, body}
      } else {
        header = "Common base editor architectures and their key properties"
        body = {
          "Name: ": "Architecture names reported here reflect the nomenclature given when first reported or as commonly used. Linker lengths may vary slightly between versions.",
          "Key properties: ": "Key properties represent distinguishing reasons for selecting that specific architecture, as described in the original publication or subsequent studies. aa = amino acids, PuroR = puromycin resistance cassette, scFv = single chain variable fragment antibody recognizing GCN4, GB1 = solubility tag, a binding domain of protein G from group G Streptococcus, DBD = single-stranded DNA binding domain from Rad51, NpuN = N-intein from N. punctiforme, NpuC = C-intein from N. punctiforme, CFN = 3 aa extein motif required by Npu-intein for splicing, nCas = nickase Cas variant, dCas = catalytically dead Cas variant, UGI = uracil glycosylase inhibitor, NLS = nuclear localization signal."
        }
        return {header, body}
      }
    }
  },
  methods: {
    tabHeadSlotName(tabName) {
      return `tab-head-${tabName}`;
    },

    switchTab(tabName) {
      this.activeTab = tabName;
    }
  }
};
</script>
